import React from 'react';
import { connect, DispatchProp, useDispatch } from 'react-redux';
import { useNavigate, Link } from 'react-router-dom';
import { SharedAppState } from '~/tools/js/Shared/stores/typings';

import { characterEnvSelectors, Constants, rulesEngineSelectors } from '@dndbeyond/character-rules-engine';

import { builderActions } from '../../actions';
import Page from '../../components/Page';
import PageHeader from '../../components/PageHeader';
import { navigationConfig } from '../../config';
import { BuilderMethod, RouteKey } from '../../constants/navigation';
import { NavigationUtils } from '../../utils';

class FakeLink extends React.PureComponent {
    render() {
        return (
            <div className="character-button-block builder-button builder-button-disabled">{this.props.children}</div>
        );
    }
}

interface Props extends DispatchProp {
    isEnabled?: boolean;
    characterIsLoading: Constants.CharacterLoadingStatusEnum;
    characterId: number;
}

const BuilderTypeChoicePage: React.FC<Props> = ({ isEnabled = true, characterIsLoading, characterId }) => {
    const [showHelpText, setShowHelpText] = React.useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (characterIsLoading === Constants.CharacterLoadingStatusEnum.LOADED) {
            navigate(NavigationUtils.getCharacterBuilderUrl(characterId));
        }
    }, [characterIsLoading, characterId, navigate]);

    let LinkComponent: React.ComponentType<any> = isEnabled ? Link : FakeLink;

    let standardOptionsClasses: Array<string> = ['builder-type-options'];
    if (!isEnabled) {
        standardOptionsClasses.push('builder-type-options-disabled');
    }

    return (
        <Page>
            <PageHeader>Character Creation Method</PageHeader>
            <div className="builder-type">
                <p className="builder-type-instructions">Choose one of three ways to create your character</p>
                <div className="builder-type-choice">
                    <div className="builder-type-choice-list">
                        <div className="builder-type-choice-item">
                            <div
                                role="button"
                                aria-label="Standard Builder"
                                onClick={() => {
                                    dispatch(builderActions.stepBuildRequest(showHelpText));
                                }}
                                className="character-button-block builder-button"
                            >
                                <div className="builder-type-button-heading">Standard</div>
                                <div className="builder-type-button-desc">
                                    Create a character by making choices using a step-by-step approach.
                                </div>
                            </div>
                            <div className={standardOptionsClasses.join(' ')}>
                                <div className="builder-type-option">
                                    <input
                                        type="checkbox"
                                        checked={showHelpText}
                                        disabled={!isEnabled}
                                        className="builder-type-option-checkbox"
                                        id="q-include-help"
                                        onChange={(evt) => {
                                            if (isEnabled) {
                                                setShowHelpText(evt.target.checked);
                                            }
                                        }}
                                    />
                                    <label className="builder-type-option-label" htmlFor="q-include-help">
                                        <span className="builder-type-option-help-icon" />
                                        Show Help Text
                                        <span className="builder-type-option-label-extra">(Ideal for Beginners)</span>
                                    </label>
                                </div>
                            </div>
                        </div>
                        <div className="builder-type-choice-item">
                            <LinkComponent
                                to={navigationConfig.getRouteDefPath(RouteKey.QUICK_BUILD)}
                                size="large"
                                onClick={() => dispatch(builderActions.builderMethodSet(BuilderMethod.QUICK))}
                                className="character-button-block builder-button"
                            >
                                <div className="builder-type-button-heading">Quick Build</div>
                                <div className="builder-type-button-desc">
                                    Create a 1st-level character using recommended starting options.
                                </div>
                            </LinkComponent>
                        </div>
                        <div className="builder-type-choice-item">
                            <LinkComponent
                                to={navigationConfig.getRouteDefPath(RouteKey.RANDOMIZE_BUILD)}
                                size="large"
                                onClick={() => dispatch(builderActions.builderMethodSet(BuilderMethod.RANDOMIZE))}
                                className="character-button-block builder-button"
                            >
                                <div className="builder-type-button-heading">Randomize</div>
                                <div className="builder-type-button-desc">
                                    Create a character of any level with random statistics by setting parameters.
                                </div>
                            </LinkComponent>
                        </div>
                    </div>
                </div>
            </div>
        </Page>
    );
};

export default connect((state: SharedAppState) => {
    return {
        characterIsLoading: characterEnvSelectors.getLoadingStatus(state),
        characterId: rulesEngineSelectors.getId(state),
    };
})(BuilderTypeChoicePage);
